/**
 * ResourceMenu.js
 * Menu de agendas da tela de Agendamentos: "Localizar profissional" (rola até a
 * coluna do profissional) e "Escolher agenda" (filtra uma só).
 * Com muitas agendas ganha um campo de busca no topo: digitar filtra a lista,
 * Enter escolhe a primeira visível e Esc fecha.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem, TextField } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { matchesSearch } from './filters/searchText';

// A partir de quantas agendas o campo de busca aparece
const SEARCH_MIN_ITEMS = 6;

const ResourceMenu = ({ anchorEl, open, items, onClose }) => {
  const [query, setQuery] = useState('');
  const searchInputRef = useRef(null);
  const searchable = items.length > SEARCH_MIN_ITEMS;

  // Cada abertura começa com a busca limpa
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const visible = useMemo(
    () => (searchable ? items.filter((item) => matchesSearch(item.label, query)) : items),
    [items, query, searchable]
  );

  const pick = (item) => {
    onClose();
    item.onPick();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[0]) pick(visible[0]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    // As setas continuam andando pela lista; o resto das teclas fica no campo
    // (senão o menu "pula" para o item que começa com a letra digitada)
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') e.stopPropagation();
  };

  return (
    <Menu
      anchorEl={anchorEl || null}
      open={open}
      onClose={onClose}
      // Com busca, o foco nasce no campo, não no primeiro item. O Menu do MUI
      // foca o painel ao abrir, então o campo é focado depois da transição.
      autoFocus={!searchable}
      disableAutoFocusItem={searchable}
      TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
      PaperProps={{ sx: { maxHeight: 360, minWidth: 240, borderRadius: 2 } }}
    >
      {searchable && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.75 }} onKeyDown={handleSearchKeyDown}>
          <TextField
            size="small"
            fullWidth
            inputRef={searchInputRef}
            placeholder="Buscar pelo nome"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ 'aria-label': 'Buscar pelo nome' }}
            InputProps={{
              startAdornment: <SearchOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8', mr: 0.75 }} />,
              sx: {
                fontSize: 13,
                borderRadius: '6px',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#CBD5E1' },
                '&.Mui-focused fieldset': { borderColor: '#ED3680', borderWidth: 1 },
              },
            }}
          />
        </Box>
      )}
      {visible.map((item) => (
        <MenuItem key={item.key} sx={{ fontSize: 13 }} onClick={() => pick(item)}>
          {item.label}
        </MenuItem>
      ))}
      {searchable && visible.length === 0 && (
        <Box sx={{ px: 2, py: 1.25, fontSize: 12, color: '#94A3B8' }}>
          Nenhum resultado para esse nome
        </Box>
      )}
    </Menu>
  );
};

export default ResourceMenu;
